export const spdPlusArticle = {
    id: 'spd-plus-module',
    title: 'SPD+ Разметка',
    category: 'Модули расширения',
    requiredModule: 'spd-plus',
    summary: 'Профессиональное ускорение: тиражирование участков встык (Shift), массовый выбор и пакетное редактирование, топология (вычитание/слияние), аудит дубликатов и откат правок.',
    markdown: `
> **SPD+ Разметка** — это пакет инструментов профессионального ускорения, автоматизирующий рутинные операции с геометрией и ускоряющий формирование мастер-планов, коттеджных посёлков и жилых кварталов в десятки раз.

---

## 🎯 Ключевые возможности

:::grid cols=2
:::card icon="copy" title="1. Быстрое дублирование участков (Shift-стыковка)"
* **Клонирование в 1 клик:** Полное копирование геометрии, категории, характеристик, фото и ссылок объекта.
* **Стыковка «грань-в-грань» (по клавише Shift):**
  * При зажатом \`Shift\` дубликат моментально примагничивается вплотную к границе исходного участка.
  * **Справа / Слева:** левая граница дубликата идеально соединяется с правой границей оригинала с точным сохранением высоты (контур не выступает за края).
  * **Сверху / Снизу:** нижняя граница дубликата соединяется с верхней границей оригинала с точным сохранением ширины.
* **Поддержка любых форм:** Алгоритм автоматически рассчитывает углы наклона для поворотных и трапециевидных участков.
* **Результат:** Нарезка квартала из 50 участков занимает 2 минуты вместо получасовой ручной обрисовки.
:::

:::card icon="layers" title="2. Массовый выбор (Shift) и групповое редактирование"
* **Выбор нескольких участков на карте:** Зажав клавишу \`Shift\`, последовательно кликайте по объектам на карте для их добавления в общую группу выделения.
* **Автоматическое открытие Менеджера объектов:** При групповом выборе сразу открывается «Менеджер объектов» со списком только выбранных участков.
* **Пакетное редактирование свойств:**
  * Массовая смена статусов (например, перевод группы участков в «В продаже», «Бронь» или «Продано»).
  * Групповое назначение категории, целевого назначения и атрибутов.
  * Пакетный экспорт выбранной группы объектов.
:::

:::card icon="tool" title="3. Топологические операции на карте"
Плавающая панель инструментов топологии для быстрого моделирования:
* **Вычитание контуров (Difference / Cut-out):**
  * Вырезание дорог, сервитутов, охранных зон или водоемов из основного земельного массива.
  * Формирование внутренних дворов и анклавов с автоматическим пересчетом полезной площади.
* **Слияние участков (Union / Merge):**
  * Бесшовное объединение 2 и более смежных участков в единый полигон.
  * Автоматическое удаление внутренних перегородок и обновление суммарной площади.
:::

:::card icon="search" title="4. Аудит дубликатов и наложений"
Глубокая проверка базы объектов прямо в Менеджере объектов:
* **4 понятные градации коллизий:**
  * 🔴 **1 в 1 (Точная копия):** совпадение контура более чем на 97% или смещение угловых точек менее чем на 50 сантиметров.
  * 🟠 **Вложенность:** один объект целиком находится внутри другого (наложение более 95% площади).
  * 🟡 **Пересечение:** частичное наложение полигонов с указанием точного процента пересечения.
  * 🔵 **Кадастровый дубликат:** одинаковый кадастровый номер у объектов с разными границами.
* **Строгая типизация:** сравнение только однотипных объектов (участки с участками, здания со зданиями).
* **Изолированный режим карты:** скрытие посторонних объектов и подсветка групп дублей контрастными цветами.
:::

:::card icon="rotate-ccw" title="5. История изменений (Undo / Redo)"
Надежная защита от случайных действий и ошибок оператора:
* **Мгновенный откат по Ctrl+Z:** Отмена последней топологической операции или геометрической правки.
* **Повтор по Ctrl+Y (Ctrl+Shift+Z):** Возврат отмененного действия.
* **Автоматические слепки состояния:** Система сохраняет моментальные снимки геометрии и атрибутов перед каждой операцией.
:::
:::

---

## 🚀 Как использовать новые инструменты

### Сценарий 1: Быстрая нарезка квартала участков
1. Выберите готовый участок на карте.
2. В появившейся панели действий нажмите кнопку **«Дублировать объект»** (иконка копирования).
3. Переместите появившийся контур в сторону следующего участка и **зажмите клавишу \`Shift\`**.
4. Дубликат автоматически встанет ровно встык к границе оригинала.
5. Нажмите **клик мыши** или клавишу **\`Enter\`** — участок зафиксирован! Повторите для следующего участка в ряду.

### Сценарий 2: Массовый выбор участков и пакетное редактирование
1. Зажмите клавишу **\`Shift\`** на клавиатуре.
2. Кликайте левой кнопкой мыши по нужным участкам на карте — они подсветятся как выбранная группа.
3. Слева автоматически откроется панель **«Менеджер объектов»** со списком только выбранных участков.
4. Выполните массовую смену статуса, категории или примените пакетный экспорт ко всем выбранным лотам разом.

### Сценарий 3: Вырезание дороги или сервитута из массива
1. Выберите основной земельный массив.
2. В панели топологии нажмите кнопку **«Вычесть контур»** (иконка ножниц).
3. Кликните на второй объект (например, дорогу или охранную зону), который нужно вырезать.
4. Система мгновенно вычтет пересечение и обновит площадь участка.

### Сценарий 4: Поиск и устранение дубликатов
1. Откройте панель **«Менеджер объектов»** слева.
2. В шапке панели нажмите кнопку **«Дубликаты»** (иконка слоев/копирования).
3. Используйте фильтры (Pills) сверху: *«🔴 1 в 1»*, *«🟠 Вложенность»*, *«🟡 Пересечение»*, *«🔵 Кадастровый»*.
4. Кликните на проблемный объект: карта подсветит группу, а в карточке настроек отобразится точная причина коллизии.
`
};
